/**
 * Samples to bytes and back, for the live setup call.
 *
 * The call carries PCM16 little-endian mono both ways: 16 kHz from the
 * microphone (`LIVE_INPUT_RATE`) and 24 kHz from the model (`LIVE_OUTPUT_RATE`).
 * The audio library speaks Float32 in the range [-1, 1] at both ends, so these
 * two functions are the whole of the conversion.
 *
 * They live apart from `live-audio.ts` for one reason: that file imports
 * `react-native-audio-api`, which no test can load (it installs a native
 * module at import time). Here the arithmetic can be proven from arrays built
 * by hand, which is where the bugs would be.
 */

/** The largest value a signed 16-bit sample can hold. */
const PEAK = 32767;
/** What a signed 16-bit sample is divided by on the way back to a float. */
const SCALE = 32768;

/**
 * One buffer of microphone samples as PCM16 little-endian bytes.
 *
 * Anything outside [-1, 1] is clamped rather than allowed to wrap: a sample
 * that overflowed would come back as the opposite sign, which is a click in
 * the model's ear. A sample that is not a number at all is silence.
 */
export const floatToPcm16 = (samples: Float32Array): Uint8Array<ArrayBuffer> => {
  const bytes = new Uint8Array(samples.length * 2);
  for (let at = 0; at < samples.length; at += 1) {
    const sample = samples[at] ?? 0;
    const held = Number.isFinite(sample) ? sample : 0;
    const clamped = held > 1 ? 1 : held < -1 ? -1 : held;
    // Two's complement falls out of the bitwise ops: JavaScript does them on
    // 32-bit integers, so -1 becomes 0xff 0xff, which is what the wire wants.
    const value = Math.round(clamped * PEAK);
    bytes[at * 2] = value & 0xff;
    bytes[at * 2 + 1] = (value >> 8) & 0xff;
  }
  return bytes;
};

/**
 * One binary frame from the server as samples the player can schedule.
 *
 * A trailing odd byte is dropped: half a sample is not a sample, and the next
 * frame starts on its own boundary.
 */
export const pcm16ToFloat = (bytes: Uint8Array): Float32Array<ArrayBuffer> => {
  const frames = Math.floor(bytes.length / 2);
  const samples = new Float32Array(frames);
  for (let at = 0; at < frames; at += 1) {
    const low = bytes[at * 2] ?? 0;
    const high = bytes[at * 2 + 1] ?? 0;
    const raw = ((high << 8) | low) & 0xffff;
    samples[at] = (raw >= 0x8000 ? raw - 0x10000 : raw) / SCALE;
  }
  return samples;
};

/**
 * The same sound at another rate, by drawing a straight line between
 * neighbouring samples.
 *
 * The recorder is asked for 16 kHz and usually obliges, but the library says
 * the device may hand over another rate, and a 48 kHz frame sent as 16 kHz
 * would reach the model three times slower than it was said. Linear
 * interpolation is enough for speech going into a recogniser; the last sample
 * is held rather than read past the end.
 */
export const resampleLinear = (samples: Float32Array, fromRate: number, toRate: number): Float32Array<ArrayBuffer> => {
  if (fromRate === toRate || samples.length === 0 || fromRate <= 0 || toRate <= 0) return Float32Array.from(samples);
  const frames = Math.max(1, Math.round((samples.length * toRate) / fromRate));
  const out = new Float32Array(frames);
  const step = fromRate / toRate;
  const last = samples.length - 1;
  for (let at = 0; at < frames; at += 1) {
    const position = at * step;
    const low = Math.min(last, Math.floor(position));
    const high = Math.min(last, low + 1);
    const between = position - low;
    out[at] = (samples[low] ?? 0) * (1 - between) + (samples[high] ?? 0) * between;
  }
  return out;
};

/** The bytes of one wire frame: a tenth of a second of PCM16 at the input rate (16 kHz). */
export const WIRE_FRAME_BYTES = (16_000 / 10) * 2;

/**
 * Joins the recorder's frames into wire frames of `bytes`. The recorder hands
 * over 50 ms on iOS (so the duck can land within 150 ms) and whatever the
 * device chooses elsewhere; the wire keeps its 100 ms frames, which the
 * server's reconnect ring and replay count in. `push` answers the whole wire
 * frames it completed, oldest first; `drop` forgets a part-built one (a gate
 * closing, a mute, a pause).
 */
export const createPcmJoiner = (bytes: number = WIRE_FRAME_BYTES): { readonly push: (pcm: Uint8Array) => Uint8Array[]; readonly drop: () => void } => {
  let held = new Uint8Array(0);
  return {
    push: (pcm) => {
      const joined = new Uint8Array(held.length + pcm.length);
      joined.set(held, 0);
      joined.set(pcm, held.length);
      const out: Uint8Array[] = [];
      let at = 0;
      while (joined.length - at >= bytes) {
        out.push(joined.slice(at, at + bytes));
        at += bytes;
      }
      held = joined.slice(at);
      return out;
    },
    drop: () => {
      held = new Uint8Array(0);
    },
  };
};
